<template>
    <div class="record-view-container">
        <router-link id="back-button" to="/" @click="clearRecord" v-if="$t('general.back').length">
            {{ $t("general.back") }}
        </router-link>
        <div
            class="record-info"
            v-if="!mediaRecorderAPi && !hasVideoContent"
            v-html="$t('record.videoPickingText', { maxDuration })"
        ></div>

        <video
            playsinline
            class="picked-video-player video-player"
            :class="{
                hidden: !pickedFileURL.length,
                inverted: this.pickedFileData === null && selectedVideoDeviceFacing === 'user'
            }"
            :poster="poster"
            ref="pickedVideoPlayer"
            preload="metadata"
        >
            {{ $t("general.html5NotSupported") }}
        </video>

        <div class="play-button-container" v-if="hasVideoContent">
            <transition name="bounce-in" appear>
                <TogglePlayButton :isPlaying="isPlaying" @click="togglePlayingVideo" />
            </transition>
        </div>

        <div v-if="hasVideoContent">
            <transition name="bounce-in" appear>
                <div class="clear-or-validate-record-container">
                    <ClearRecordButton @click="clearRecord" />
                    <ValidateRecordButton @click="onValidateButtonClicked" />
                </div>
            </transition>
        </div>

        <Message
            v-if="isMessage"
            :isError="messageOverlay.isError"
            :info="messageOverlay.info"
            :title="messageOverlay.title"
            :text="messageOverlay.text"
            :isCompliant="messageOverlay.isCompliant"
            @close="isMessage = false"
        />
        <transition name="fade" appear>
            <div class="toast" v-if="toastMessage.length">
                {{ toastMessage }}
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import "video.js/dist/video-js.css";
import { defineComponent } from "vue";
import videojs from "video.js";

import "recordrtc";
import "videojs-record";

import TogglePlayButton from "@/components/buttons/TogglePlayButton.vue";
import ClearRecordButton from "@/components/buttons/ClearRecordButton.vue";
import ValidateRecordButton from "@/components/buttons/ValidateRecordButton.vue";
import Message from "@/components/Message.vue";
import { setPoster } from "@/services/PosterGenerator";

import { isIOS, getAndroidVersion, mobileHelper } from "@/utils";

import { defaultMaxVideoDuration } from "@/constants";
import { mapState } from "vuex";
import { State } from "@/store";
import { ProjectAuthType } from "@/typings/AuthType";

export default defineComponent({
    name: "Record",
    props: {
        session: String
    },
    components: {
        TogglePlayButton,
        ClearRecordButton,
        ValidateRecordButton,
        Message
    },
    data() {
        return {
            showInfo: true,
            messageOverlay: {},
            isMessage: false,
            toastMessage: "",
            toastTimer: null,
            player: null as null | videojs.Player,
            isRecordReady: false,
            isRecording: false,
            recordDuration: 0,
            recordTimer: null,

            videoDevices: [],
            selectedVideoDeviceIndex: null as number | null,

            videoPickTimer: null as null | number,
            pickedFileURL: "",
            pickedFileData: null as File | null,
            videoData: null as File | null,
            isPlaying: false,

            mediaRecorderAPi: true,
            isIOS: isIOS(),
            isMessenger:
                navigator.userAgent.indexOf("FBAN") > -1 ||
                navigator.userAgent.indexOf("FBAV") > -1,
            isChrome: mobileHelper.isChrome,
            poster: null
        };
    },

    mounted() {
        if (!this.qrcodeObject) {
            this.$router.replace("/");
            return;
        }

        this.mediaRecorderAPi =
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            //@ts-ignore
            typeof MediaRecorder !== "undefined";

        this.mediaRecorderAPi = this.mediaRecorderAPi && !this.isMessenger;
        this.mediaRecorderAPi = this.mediaRecorderAPi && !this.iOS!;

        if (!this.qrcodeObject) {
            this.$router.replace("/");
            return;
        }

        if (this.videoFileState && this.videoNameState) {
            this.$refs.pickedVideoPlayer.addEventListener("play", this.onPlayStart);
            this.$refs.pickedVideoPlayer.addEventListener("pause", this.onPlayStop);
            this.$refs.pickedVideoPlayer.addEventListener("ended", this.onPlayStop);
            this.onVideoPicked(this.videoFileState);
        }
    },

    beforeUnmount() {
        this.isRecordReady = false;
        if (this.player) {
            setTimeout(() => this.player.dispose(), 500);
        }
    },

    computed: {
        hasVideoContent() {
            return this.pickedFileURL.length > 0 || this.videoData !== null;
        },
        canReadOnVideoJs() {
            return !isIOS() && getAndroidVersion() < 11;
        },
        selectedVideoDeviceFacing() {
            if (!this.videoDevices.length) {
                return "environment";
            }

            const device = this.videoDevices[this.selectedVideoDeviceIndex];
            return this.getVideoDeviceFacing(device);
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            maxDuration: (state: State) =>
                state.qrcode.qrcodeObject.project.burningVideoMaxLength || defaultMaxVideoDuration,
            videoFileState: (state: State) => state.record.videoFile,
            videoNameState: (state: State) => state.record.videoName
        })
    },

    methods: {
        getVideoDeviceFacing(device) {
            if (device.getCapabilities) {
                const capabilities = device.getCapabilities();
                if (capabilities.facingMode && capabilities.facingMode.length) {
                    return capabilities.facingMode[0];
                }
            }

            const facingMode =
                device.label.toLowerCase().indexOf("front") !== -1 ? "user" : "environment";

            return facingMode;
        },
        showMessage(_isError, title, text, isCompatible = true) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text,
                isCompliant: isCompatible
            };
            if (_isError) {
                console.error(text);
            }
            this.isMessage = true;
        },
        onServerError(e) {
            console.error("onServerError " + e);
            this.showMessage(true, this.$t("general.serverError", e));
        },
        pauseVideo() {
            try {
                this.player.pause();
            } catch (e) {
                console.log("Error while pausing video");
            }
        },

        switchCamera() {
            if (getAndroidVersion() >= 11) {
                this.stopDevice();
            }

            this.selectedVideoDeviceIndex =
                (this.selectedVideoDeviceIndex + 1) % this.videoDevices.length;

            this.player
                .record()
                .setVideoInput(this.videoDevices[this.selectedVideoDeviceIndex].deviceId);

            this.showToast(`${this.$t("general.camera")} ${this.selectedVideoDeviceIndex + 1}`);
        },

        onPlayStart() {
            this.isPlaying = true;
        },

        onPlayStop() {
            this.isPlaying = false;
        },

        togglePlayingVideo() {
            if (!this.hasVideoContent) {
                console.log("no video to play");
                return;
            }

            const player = this.pickedFileURL ? this.$refs.pickedVideoPlayer : this.player;

            if (!this.isPlaying) {
                player.play();
            } else {
                player.pause();

                // videojs player
                if (!this.pickedFileURL) {
                    player.currentTime(0);
                } else {
                    // html <video> element
                    player.currentTime = 0;
                }
            }
        },

        clearRecord() {
            if (this.pickedFileURL) {
                URL.revokeObjectURL(this.pickedFileURL);
                this.pickedFileURL = "";
                this.$refs.pickedVideoPlayer.src = "";
            }

            if (this.videoData) {
                this.videoData = null;
            }

            if (this.pickedFileData) {
                this.pickedFileData = null;
            }

            this.$store.dispatch("record/setRecordedVideo", {
                videoFile: null,
                videoName: ""
            });

            setTimeout(() => {
                this.$router.push("/");
            }, 1800);
        },

        async onVideoPicked(video: File) {
            // cancel the onFocus listener which is here to detect when user cancel file selection
            document.body.onfocus = null;

            if (this.videoPickTimer) {
                clearTimeout(this.videoPickTimer);
                this.videoPickTimer = null;
            }
            this.pauseVideo();
            // release camera & micro
            const parts = video.name.split(".");
            const extension = parts[parts.length - 1].toLowerCase();
            const possibleExtensions = ["mp4", "mov", "avi", "mpg"];
            if (possibleExtensions.includes(extension)) {
                this.$refs.pickedVideoPlayer.addEventListener(
                    "loadedmetadata",
                    this.checkPickedVideoDuration
                );
                this.pickedFileURL = URL.createObjectURL(video);
                this.pickedFileData = video;
                this.$refs.pickedVideoPlayer.src = this.pickedFileURL;
                this.poster = await setPoster(this.$refs.pickedVideoPlayer);
            } else {
                this.showMessage(true, this.$t("record.unsupported"), this.$t("record.available"));
            }
        },

        stopDevice() {
            // release camera & micro
            // Fix for a bug on chrome android 11
            // https://support.google.com/chrome/thread/92054311?hl=en
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1138823&q=webrtc%20android%2011%20chrome&can=2
            if (getAndroidVersion() >= 11) {
                (() => {
                    const stream = this.player.record().stream;

                    if (!stream) {
                        console.log("no stream to stop");
                        return;
                    }

                    const player = this.$refs.videoJsContainer.getElementsByTagName("video")[0];
                    if (player.srcObject) {
                        player.srcObject = null;
                    }

                    stream.getTracks().forEach(track => {
                        stream.removeTrack(track);
                        track.stop();
                    });

                    this.onPlayStop();
                })();
            }

            this.player.record().stopDevice();
        },

        checkPickedVideoDuration() {
            this.$refs.pickedVideoPlayer.removeEventListener(
                "loadedmetadata",
                this.checkPickedVideoDuration
            );
            const duration = this.$refs.pickedVideoPlayer.duration;

            // video too long
            if (duration > this.maxDuration) {
                this.showMessage(
                    false,
                    this.$t("record.videoTooLongTitle"),
                    this.$t("record.videoTooLongText", {
                        maxDuration: this.maxDuration
                    })
                );
                this.clearRecord();
            }
        },

        onValidateButtonClicked() {
            try {
                this.player.pause();
            } catch (e) {
                console.warn(e);
            }

            const date = new Date();
            const formattedDate = `${date.getDate()}/${date.getMonth() +
                1}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}`;
            this.uploadVideo(`video ${formattedDate}`);
        },

        async uploadVideo() {
            if (!this.videoData && !this.pickedFileData) {
                console.log("No recorded video");
                return;
            }

            try {
                switch (this.qrcodeObject.project.authTypeId) {
                    case ProjectAuthType.NONE:
                        // show user data fields if any is required
                        if (this.qrcodeObject.project.shortlinkBurnFields &&
                            this.qrcodeObject.project.shortlinkBurnFields.find(
                                    field => field.isRequired
                                )
                        ) {
                            this.$router.replace("user-data");
                        } else{
                            this.$router.replace("upload");
                        }
                        break;
                    case ProjectAuthType.OPTIONAL:
                        this.$router.replace("optional-signup");
                        break;

                    case ProjectAuthType.REQUIRED:
                    default:
                        if (this.$store.state.auth.authenticated) {
                            this.$router.replace("Upload");
                        } else {
                            this.$router.replace("SignUp");
                        }
                }
            } catch (e) {
                // if editing video, only signIn
                if (!this.qrcodeObject?.isOpen) {
                    this.$router.replace("SignIn");
                } else {
                    this.$router.replace("SignUp");
                }
            }
        },

        showToast(message, duration = 3000) {
            this.toastMessage = message;
            if (this.toastTimer) {
                clearTimeout(this.toastTimer);
            }
            this.toastTimer = setTimeout(() => {
                this.toastMessage = "";
                this.toastTimer = null;
            }, duration);
        }
    }
});
</script>
<style scoped>
#back-button {
    position: absolute;
    top: 20px;
    left: 20px;
    font-weight: bold;
    text-decoration: none;
    color: var(--white);
    z-index: 2;
}

.record-view-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--black);
}

.record-info {
    position: absolute;
    width: 90vw;
    height: 10vh;
    top: 20vh;
    left: 5vw;
    right: 0;
    color: var(--white);
    text-align: center;
    font-size: 1.5em;
    z-index: 2;
}

.video-js-container.inverted {
    transform: scaleX(-1);
}

.video-js-container.hidden {
    opacity: 0;
}

.video-js-container.ready {
    opacity: 1;
}

.play-button-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.clear-or-validate-record-container {
    position: absolute;
    bottom: 15%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
}

.recording-controls-container {
    position: absolute;
    bottom: 10%;
    left: 50%;
    transform: translateX(-50%);
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
    justify-items: center;
}

.video-player {
    opacity: 1;
    transition: opacity 1.5s linear;
}

.video-player.hidden {
    opacity: 0;
}

.picked-video-player {
    position: absolute;
    transition: 0s;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
}

.picked-video-player.inverted {
    transform: translateY(-50%) scaleX(-1);
}

.camera-switcher {
    position: absolute;
    bottom: 10%;
    right: 15%;
}

.video-picker {
    position: absolute;
    bottom: 10%;
    left: 15%;
}

.video-picker.centered {
    transition: 0s;
    position: absolute;
    bottom: 10%;
    left: calc(50vw - 40px);
    width: 80px;
    height: 80px;
}

.pending-screen {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    transition: opacity 0.2s ease-in;
    z-index: 3;
}

.experience-name-popin {
    transition: opacity 0.2s ease-in;
}

.toast {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.8);
    border-radius: 4vh;
    line-height: 2vh;
    font-size: 2vh;
    color: var(--black);
    padding: 10px 20px;
}
</style>
